<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-md-8">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex align-items-center">
              <p class="mb-0">Configuración</p>
              <argon-button
                color="success"
                size="sm"
                class="ms-auto"
                @click="save"
                :disabled="isSaving"
              >
                Guardar
              </argon-button>
            </div>
          </div>
          <div class="card-body">
            <p class="text-uppercase text-sm">Información de la empresa</p>
            <div class="row">
              <div class="col-md-12">
                <label class="form-control-label">Nombre de la empresa</label>
                <argon-input type="text" v-model="form.company_name" placeholder="Ej: Mi Empresa" />
              </div>
              <div class="col-md-12">
                <label class="form-control-label">NIT</label>
                <argon-input type="text" v-model="form.nit" placeholder="Número de identificación tributaria" />
              </div>
              <div class="col-md-12">
                <label class="form-control-label">Descripción</label>
                <textarea
                  class="form-control"
                  v-model="form.description"
                  rows="3"
                  placeholder="Descripción o notas"
                ></textarea>
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Correo electrónico</label>
                <argon-input type="email" v-model="form.email" placeholder="contacto@empresa.com" />
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Teléfono</label>
                <argon-input type="text" v-model="form.phone" placeholder="+57 300 123 4567" />
              </div>
              <div class="col-md-12">
                <label class="form-control-label">Dirección</label>
                <argon-input type="text" v-model="form.address" placeholder="Dirección física" />
              </div>
              <div class="col-md-6">
                <label class="form-control-label">Tasa de cambio a dólar (USD)</label>
                <input
                  type="number"
                  v-model="form.usd_exchange_rate"
                  class="form-control"
                  step="any"
                  min="0"
                  placeholder="Ej: 4200.00"
                />
                <p class="text-xs text-muted mt-1 mb-0">Valor de 1 USD en la moneda local.</p>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Beneficios de tareas</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.benefits_of_tasks"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Chat AI</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.chat_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.chat_enabled" class="mt-3">
                  <label class="form-control-label">API Key de Gemini</label>
                  <argon-input
                    type="password"
                    v-model="form.gemini_api_key"
                    :placeholder="hasGeminiApiKey ? '•••••••••••••••• (dejar vacío para mantener)' : 'AIza...'"
                  />
                  <p class="text-xs text-muted mt-2 mb-0">
                    Obtén tu key en
                    <a href="https://aistudio.google.com/apikey" target="_blank" rel="noopener noreferrer">
                      Google AI Studio
                    </a>.
                  </p>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Shopify</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.shopify_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.shopify_enabled" class="mt-3">
                  <label class="form-control-label">Dominio de la tienda</label>
                  <argon-input
                    type="text"
                    v-model="form.shopify_store"
                    placeholder="mi-tienda.myshopify.com"
                  />
                  <p class="text-xs text-muted mt-2 mb-0">
                    POST
                    <code>https://{{ shopifyStoreSlug || 'nombre-de-tu-tienda' }}.myshopify.com/admin/oauth/access_token</code>
                    con <code>grant_type=client_credentials</code>
                  </p>

                  <label class="form-control-label mt-3">Client ID</label>
                  <argon-input
                    type="text"
                    v-model="form.shopify_client_id"
                    placeholder="Client ID de tu app Shopify"
                  />

                  <label class="form-control-label mt-3">Client Secret</label>
                  <argon-input
                    type="password"
                    v-model="form.shopify_client_secret"
                    :placeholder="hasShopifyClientSecret ? '•••••••••••••••• (dejar vacío para mantener)' : 'Client Secret'"
                  />

                  <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
                    <argon-button
                      color="info"
                      size="sm"
                      :disabled="isGeneratingToken"
                      @click="generateShopifyToken"
                    >
                      <span v-if="isGeneratingToken">Generando...</span>
                      <span v-else>Generar token</span>
                    </argon-button>
                    <argon-button
                      color="secondary"
                      size="sm"
                      :disabled="isLoadingPaymentModes"
                      @click="viewShopifyPaymentModes"
                    >
                      <span v-if="isLoadingPaymentModes">Consultando...</span>
                      <span v-else>Ver modos de pago</span>
                    </argon-button>
                    <span
                      class="text-xs"
                      :class="hasShopifyToken ? 'text-success' : 'text-muted'"
                    >
                      {{ hasShopifyToken ? 'Token generado y guardado' : 'Aún no hay token generado' }}
                    </span>
                  </div>
                  <p class="text-xs text-muted mt-2 mb-0">
                    POST
                    <code>https://{{ shopifyStoreSlug || 'nombre-de-tu-tienda' }}.myshopify.com/payments_apps/api/2026-04/graphql.json</code>
                  </p>

                  <p class="text-xs text-muted mt-2 mb-0">
                    Guarda las credenciales y pulsa <strong>Generar token</strong>.
                    Se enviará <code>application/x-www-form-urlencoded</code> con Client ID y Client Secret.
                  </p>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <p class="text-uppercase text-sm">Integración ConEnvios</p>
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">ConEnvios</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.conenvios_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.conenvios_enabled" class="mt-3">
                  <label class="form-control-label">Email de ConEnvios</label>
                  <argon-input
                    type="email"
                    v-model="form.conenvios_email"
                    placeholder="correo@conenvios.com"
                  />

                  <label class="form-control-label mt-3">Contraseña de ConEnvios</label>
                  <argon-input
                    type="password"
                    v-model="form.conenvios_password"
                    :placeholder="hasConenviosPassword ? '•••••••••••••••• (dejar vacío para mantener)' : 'Contraseña'"
                  />
                  <p class="text-xs text-muted mt-2 mb-0">
                    Las credenciales se usan para generar el Bearer token de la API de ConEnvios al calcular envíos y generar guías.
                  </p>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <p class="text-uppercase text-sm">Integración KUBAPP</p>
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">KUBAPP</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.kubapp_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.kubapp_enabled" class="mt-3">
                  <label class="form-control-label">Usuario de KUBAPP</label>
                  <argon-input
                    type="text"
                    v-model="form.kubapp_user_id"
                    placeholder="Ej: usuario_admin"
                  />

                  <label class="form-control-label mt-3">Contraseña de KUBAPP</label>
                  <argon-input
                    type="password"
                    v-model="form.kubapp_pass_wd"
                    :placeholder="hasKubappPassWd ? '•••••••••••••••• (dejar vacío para mantener)' : 'Contraseña'"
                  />

                  <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
                    <argon-button
                      color="info"
                      size="sm"
                      :disabled="isGeneratingKubappToken"
                      @click="generateKubappToken"
                    >
                      <span v-if="isGeneratingKubappToken">Generando...</span>
                      <span v-else>Generar token</span>
                    </argon-button>
                    <span v-if="hasKubappToken" class="text-xs text-success">
                      <i class="fas fa-check-circle"></i> Token generado
                    </span>
                  </div>
                  <p class="text-xs text-muted mt-2 mb-0">
                    Las credenciales se usan para generar el token de la API de KUBAPP.
                  </p>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <p class="text-uppercase text-sm">Integración KUBAPP VENEZUELA</p>
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">KUBAPP VENEZUELA</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.kubapp_ve_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.kubapp_ve_enabled" class="mt-3">
                  <label class="form-control-label">Correo de KUBAPP VENEZUELA</label>
                  <argon-input
                    type="email"
                    v-model="form.kubapp_ve_email"
                    placeholder="correo@kubapp.com"
                  />

                  <label class="form-control-label mt-3">Contraseña de KUBAPP VENEZUELA</label>
                  <argon-input
                    type="password"
                    v-model="form.kubapp_ve_password"
                    :placeholder="hasKubappVePassword ? '•••••••••••••••• (dejar vacío para mantener)' : 'Contraseña'"
                  />

                  <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
                    <argon-button
                      color="info"
                      size="sm"
                      :disabled="isGeneratingKubappVeToken"
                      @click="generateKubappVeToken"
                    >
                      <span v-if="isGeneratingKubappVeToken">Generando...</span>
                      <span v-else>Generar token</span>
                    </argon-button>
                    <span v-if="hasKubappVeToken" class="text-xs text-success">
                      <i class="fas fa-check-circle"></i> Token generado
                    </span>
                  </div>
                  <p class="text-xs text-muted mt-2 mb-0">
                    Las credenciales se usan para generar el token de la API de KUBAPP VENEZUELA.
                  </p>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <p class="text-uppercase text-sm">Integración Shopify Venezuela</p>
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Shopify Venezuela</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.shopify_ve_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <div v-if="form.shopify_ve_enabled" class="mt-3">
                  <label class="form-control-label">Dominio de la tienda</label>
                  <argon-input
                    type="text"
                    v-model="form.shopify_ve_store"
                    placeholder="mi-tienda-ve.myshopify.com"
                  />

                  <label class="form-control-label mt-3">Client ID</label>
                  <argon-input
                    type="text"
                    v-model="form.shopify_ve_client_id"
                    placeholder="Client ID de tu app Shopify Venezuela"
                  />

                  <label class="form-control-label mt-3">Client Secret</label>
                  <argon-input
                    type="password"
                    v-model="form.shopify_ve_client_secret"
                    :placeholder="hasShopifyVeClientSecret ? '•••••••••••••••• (dejar vacío para mantener)' : 'Client Secret'"
                  />

                  <p class="text-xs text-muted mt-2 mb-0">
                    Las credenciales se usan para la integración OAuth de Shopify para Venezuela.
                  </p>

                  <div class="d-flex align-items-center gap-2 mt-3 flex-wrap">
                    <argon-button
                      color="info"
                      size="sm"
                      :disabled="isGeneratingShopifyVeToken"
                      @click="generateShopifyVeToken"
                    >
                      <span v-if="isGeneratingShopifyVeToken">Generando...</span>
                      <span v-else>Generar token</span>
                    </argon-button>
                    <span
                      class="text-xs"
                      :class="hasShopifyVeToken ? 'text-success' : 'text-muted'"
                    >
                      <i v-if="hasShopifyVeToken" class="fas fa-check-circle"></i>
                      {{ hasShopifyVeToken ? 'Token generado y guardado' : 'Aún no hay token generado' }}
                    </span>
                  </div>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <p class="text-uppercase text-sm">Integración Siigo</p>
                <div class="d-flex align-items-center">
                  <label class="form-control-label mb-0">Activar sincronización Siigo</label>
                  <div class="form-check form-switch ms-3">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      v-model="form.siigo_sync_enabled"
                    />
                    <label class="form-check-label">Activar</label>
                  </div>
                </div>
                <p class="text-sm text-muted mt-2 mb-0">
                  El access key se configura en el servidor (.env). Desde aquí indica usuario y Partner ID.
                </p>
                <div v-if="form.siigo_sync_enabled" class="row mt-3">
                  <div class="col-md-6">
                    <label class="form-control-label">Usuario Siigo</label>
                    <argon-input
                      type="text"
                      v-model="form.siigo_username"
                      placeholder="Ej: correo@empresa.com"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-control-label">Partner ID</label>
                    <argon-input
                      type="text"
                      v-model="form.siigo_partner_id"
                      placeholder="Ej: miStock"
                    />
                  </div>
                </div>
              </div>

              <div class="col-md-12 mt-3">
                <hr class="horizontal dark" />
                <p class="text-uppercase text-sm">Logos (opcional)</p>
                <div class="row">
                  <div class="col-md-6">
                    <label class="form-control-label">Ícono / Logo claro</label>
                    <input
                      type="file"
                      class="form-control"
                      accept="image/*"
                      @change="onLogoLightChange"
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-control-label">Ícono / Logo oscuro</label>
                    <input
                      type="file"
                      class="form-control"
                      accept="image/*"
                      @change="onLogoDarkChange"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-md-4" v-if="form.siigo_sync_enabled">
        <div class="card">
          <div class="card-header pb-0">
            <p class="mb-0 text-uppercase text-sm">Webhooks Siigo</p>
          </div>
          <div class="card-body">
            <p class="text-xs text-muted">
              Suscripciones a eventos de Siigo (<code>POST /v1/webhooks</code>).
            </p>

            <div class="mb-3">
              <label class="form-control-label">Application ID</label>
              <argon-input
                type="text"
                v-model="webhookForm.application_id"
                placeholder="Siigo"
              />
            </div>

            <div class="mb-3">
              <label class="form-control-label">Topic</label>
              <select v-model="webhookForm.topic" class="form-select form-select-sm">
                <option
                  v-for="topic in siigoWebhookTopics"
                  :key="topic.value"
                  :value="topic.value"
                >
                  {{ topic.label }}
                </option>
              </select>
            </div>

            <div class="mb-3">
              <label class="form-control-label">URL del webhook</label>
              <argon-input
                type="url"
                v-model="webhookForm.url"
                placeholder="https://tu-dominio.com/api/siigo/webhooks/products"
              />
            </div>

            <argon-button
              color="info"
              size="sm"
              class="w-100"
              :disabled="webhookSaving"
              @click="createWebhook"
            >
              <i :class="webhookSaving ? 'fas fa-sync fa-spin me-1' : 'fas fa-plus me-1'"></i>
              Crear suscripción
            </argon-button>

            <hr class="horizontal dark my-3" />

            <p class="text-uppercase text-xs text-muted mb-2">Suscripciones registradas</p>

            <div v-if="webhookLoading" class="text-center py-3 text-muted text-sm">
              <i class="fas fa-spinner fa-spin"></i> Cargando...
            </div>

            <p v-else-if="!siigoWebhooks.length" class="text-sm text-muted mb-0">
              No hay webhooks registrados.
            </p>

            <ul v-else class="list-group list-group-flush">
              <li
                v-for="hook in siigoWebhooks"
                :key="hook.id"
                class="list-group-item px-0 py-2 border-0 border-bottom"
              >
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div class="min-w-0">
                    <p class="text-xs text-muted mb-0 text-truncate" :title="hook.id">{{ hook.id }}</p>
                    <p class="text-sm mb-0 fw-bold">{{ hook.topic }}</p>
                    <p class="text-xs text-muted mb-0 text-break">{{ hook.url }}</p>
                  </div>
                  <button
                    type="button"
                    class="btn btn-link text-danger p-0 ms-1 flex-shrink-0"
                    title="Eliminar webhook"
                    :disabled="webhookDeletingId === hook.id"
                    @click="deleteWebhook(hook.id)"
                  >
                    <i :class="webhookDeletingId === hook.id ? 'fas fa-sync fa-spin' : 'fas fa-trash'"></i>
                  </button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import shopifyService from "@/services/shopify.service";
import shopifyVeService from "@/services/shopifyVenezuela.service";
import settingsService from "@/services/settings.service";
import Swal from "sweetalert2";

export default {
  name: "Settings",
  components: {
    ArgonInput,
    ArgonButton,
  },
  data() {
    return {
      isSaving: false,
      isGeneratingToken: false,
      isGeneratingKubappToken: false,
      isGeneratingKubappVeToken: false,
      isLoadingPaymentModes: false,
      form: {
        company_name: "",
        nit: "",
        description: "",
        email: "",
        phone: "",
        address: "",
        usd_exchange_rate: "",
        benefits_of_tasks: false,
        chat_enabled: false,
        gemini_api_key: "",
        shopify_enabled: false,
        shopify_store: "",
        shopify_client_id: "",
        shopify_client_secret: "",
        siigo_sync_enabled: false,
        siigo_username: "",
        siigo_partner_id: "",
        conenvios_enabled: false,
        conenvios_email: "",
        conenvios_password: "",
        kubapp_enabled: false,
        kubapp_user_id: "",
        kubapp_pass_wd: "",
        kubapp_ve_enabled: false,
        kubapp_ve_email: "",
        kubapp_ve_password: "",
        shopify_ve_enabled: false,
        shopify_ve_store: "",
        shopify_ve_client_id: "",
        shopify_ve_client_secret: "",
      },
      hasGeminiApiKey: false,
      hasShopifyToken: false,
      hasShopifyClientSecret: false,
      hasConenviosPassword: false,
      hasKubappPassWd: false,
      hasKubappToken: false,
      hasKubappVePassword: false,
      hasKubappVeToken: false,
      hasShopifyVeClientSecret: false,
      hasShopifyVeToken: false,
      isGeneratingShopifyVeToken: false,
      logoLightFile: null,
      logoDarkFile: null,
      webhookLoading: false,
      webhookSaving: false,
      webhookDeletingId: null,
      siigoWebhooks: [],
      webhookForm: {
        application_id: "Siigo",
        topic: "public.siigoapi.products.create",
        url: "",
      },
      siigoWebhookTopics: [
        { value: "public.siigoapi.products.create", label: "Productos — crear" },
        { value: "public.siigoapi.products.update", label: "Productos — actualizar" },
        { value: "public.siigoapi.products.delete", label: "Productos — eliminar" },
        { value: "public.siigoapi.invoices.create", label: "Facturas — crear" },
        { value: "public.siigoapi.invoices.update", label: "Facturas — actualizar" },
      ],
    };
  },
  mounted() {
    this.loadSettings();
    this.loadSiigoWebhooks();
  },
  computed: {
    shopifyStoreSlug() {
      const store = (this.form.shopify_store || "").trim().toLowerCase();
      if (!store) return "";
      return store.replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.myshopify\.com$/, "");
    },
  },
  methods: {
    async loadSettings() {
      try {
        const response = await this.$store.dispatch("settings/get");
        const data = response.data;
        this.form = {
          company_name: data.company_name || "",
          nit: data.nit || "",
          description: data.description || "",
          email: data.email || "",
          phone: data.phone || "",
          address: data.address || "",
          usd_exchange_rate: data.usd_exchange_rate ?? "",
          benefits_of_tasks: !!data.benefits_of_tasks,
          chat_enabled: !!data.chat_enabled,
          gemini_api_key: "",
          shopify_enabled: !!data.shopify_enabled,
          shopify_store: data.shopify_store || "",
          shopify_client_id: data.shopify_client_id || "",
          shopify_client_secret: "",
          siigo_sync_enabled: !!data.siigo_sync_enabled,
          siigo_username: data.siigo_username || "",
          siigo_partner_id: data.siigo_partner_id || "",
          conenvios_enabled: !!data.conenvios_enabled,
          conenvios_email: data.conenvios_email || "",
          conenvios_password: "",
          kubapp_enabled: !!data.kubapp_enabled,
          kubapp_user_id: data.kubapp_user_id || "",
          kubapp_pass_wd: "",
          kubapp_ve_enabled: !!data.kubapp_ve_enabled,
          kubapp_ve_email: data.kubapp_ve_email || "",
          kubapp_ve_password: "",
          shopify_ve_enabled: !!data.shopify_ve_enabled,
          shopify_ve_store: data.shopify_ve_store || "",
          shopify_ve_client_id: data.shopify_ve_client_id || "",
          shopify_ve_client_secret: "",
        };
        this.hasGeminiApiKey = !!data.has_gemini_api_key;
        this.hasShopifyToken = !!data.has_shopify_token;
        this.hasShopifyClientSecret = !!data.has_shopify_client_secret;
        this.hasConenviosPassword = !!data.has_conenvios_password;
        this.hasKubappPassWd = !!data.has_kubapp_pass_wd;
        this.hasKubappToken = !!data.has_kubapp_token;
        this.hasKubappVePassword = !!data.has_kubapp_ve_password;
        this.hasKubappVeToken = !!data.has_kubapp_ve_token;
        this.hasShopifyVeClientSecret = !!data.has_shopify_ve_client_secret;
        this.hasShopifyVeToken = !!data.has_shopify_ve_token;
      } catch (error) {
        console.error("Error al cargar la configuración:", error);
        await Swal.fire("Error", "Error al cargar la configuración", "error");
      }
    },
    onLogoLightChange(event) {
      this.logoLightFile = event.target.files?.[0] || null;
    },
    onLogoDarkChange(event) {
      this.logoDarkFile = event.target.files?.[0] || null;
    },
    async save() {
      if (this.form.chat_enabled && !this.form.gemini_api_key.trim() && !this.hasGeminiApiKey) {
        await Swal.fire(
          "Error",
          "Ingresa la API key de Gemini para activar Chat AI.",
          "error"
        );
        return;
      }

      if (this.form.shopify_enabled && !this.form.shopify_store.trim()) {
        await Swal.fire(
          "Error",
          "Ingresa el dominio de la tienda Shopify.",
          "error"
        );
        return;
      }

      if (this.form.shopify_enabled && !this.form.shopify_client_id.trim()) {
        await Swal.fire("Error", "Ingresa el Client ID de Shopify.", "error");
        return;
      }

      if (
        this.form.shopify_enabled &&
        !this.form.shopify_client_secret.trim() &&
        !this.hasShopifyClientSecret
      ) {
        await Swal.fire("Error", "Ingresa el Client Secret de Shopify.", "error");
        return;
      }

      if (this.form.shopify_ve_enabled && !this.form.shopify_ve_store.trim()) {
        await Swal.fire("Error", "Ingresa el dominio de la tienda Shopify Venezuela.", "error");
        return;
      }

      if (this.form.shopify_ve_enabled && !this.form.shopify_ve_client_id.trim()) {
        await Swal.fire("Error", "Ingresa el Client ID de Shopify Venezuela.", "error");
        return;
      }

      if (
        this.form.shopify_ve_enabled &&
        !this.form.shopify_ve_client_secret.trim() &&
        !this.hasShopifyVeClientSecret
      ) {
        await Swal.fire("Error", "Ingresa el Client Secret de Shopify Venezuela.", "error");
        return;
      }

      if (this.form.conenvios_enabled && !this.form.conenvios_email.trim()) {
        await Swal.fire("Error", "Ingresa el email de ConEnvios.", "error");
        return;
      }

      if (
        this.form.conenvios_enabled &&
        !this.form.conenvios_password.trim() &&
        !this.hasConenviosPassword
      ) {
        await Swal.fire("Error", "Ingresa la contraseña de ConEnvios.", "error");
        return;
      }

      if (this.form.kubapp_enabled && !this.form.kubapp_user_id.trim()) {
        await Swal.fire("Error", "Ingresa el usuario de KUBAPP.", "error");
        return;
      }

      if (
        this.form.kubapp_enabled &&
        !this.form.kubapp_pass_wd.trim() &&
        !this.hasKubappPassWd
      ) {
        await Swal.fire("Error", "Ingresa la contraseña de KUBAPP.", "error");
        return;
      }

      if (this.form.kubapp_ve_enabled && !this.form.kubapp_ve_email.trim()) {
        await Swal.fire("Error", "Ingresa el correo de KUBAPP VENEZUELA.", "error");
        return;
      }

      if (
        this.form.kubapp_ve_enabled &&
        !this.form.kubapp_ve_password.trim() &&
        !this.hasKubappVePassword
      ) {
        await Swal.fire("Error", "Ingresa la contraseña de KUBAPP VENEZUELA.", "error");
        return;
      }

      this.isSaving = true;
      try {
        const formData = new FormData();
        formData.append("company_name", this.form.company_name);
        formData.append("nit", this.form.nit ?? "");
        formData.append("description", this.form.description);
        formData.append("email", this.form.email);
        formData.append("phone", this.form.phone);
        formData.append("address", this.form.address);
        formData.append("usd_exchange_rate", this.form.usd_exchange_rate ?? "");
        formData.append("benefits_of_tasks", this.form.benefits_of_tasks ? "1" : "0");
        formData.append("chat_enabled", this.form.chat_enabled ? "1" : "");
        if (this.form.gemini_api_key.trim()) {
          formData.append("gemini_api_key", this.form.gemini_api_key.trim());
        }
        formData.append("shopify_enabled", this.form.shopify_enabled ? "1" : "");
        formData.append("shopify_store", this.form.shopify_store.trim());
        formData.append("shopify_client_id", this.form.shopify_client_id.trim());
        if (this.form.shopify_client_secret.trim()) {
          formData.append("shopify_client_secret", this.form.shopify_client_secret.trim());
        }
        formData.append("siigo_sync_enabled", this.form.siigo_sync_enabled ? "1" : "0");
        formData.append("siigo_username", this.form.siigo_username.trim());
        formData.append("siigo_partner_id", this.form.siigo_partner_id.trim());
        formData.append("conenvios_enabled", this.form.conenvios_enabled ? "1" : "");
        formData.append("conenvios_email", this.form.conenvios_email.trim());
        if (this.form.conenvios_password.trim()) {
          formData.append("conenvios_password", this.form.conenvios_password.trim());
        }
        formData.append("kubapp_enabled", this.form.kubapp_enabled ? "1" : "");
        formData.append("kubapp_user_id", this.form.kubapp_user_id.trim());
        if (this.form.kubapp_pass_wd.trim()) {
          formData.append("kubapp_pass_wd", this.form.kubapp_pass_wd.trim());
        }
        formData.append("kubapp_ve_enabled", this.form.kubapp_ve_enabled ? "1" : "");
        formData.append("kubapp_ve_email", this.form.kubapp_ve_email.trim());
        if (this.form.kubapp_ve_password.trim()) {
          formData.append("kubapp_ve_password", this.form.kubapp_ve_password.trim());
        }
        formData.append("shopify_ve_enabled", this.form.shopify_ve_enabled ? "1" : "");
        formData.append("shopify_ve_store", this.form.shopify_ve_store.trim());
        formData.append("shopify_ve_client_id", this.form.shopify_ve_client_id.trim());
        if (this.form.shopify_ve_client_secret.trim()) {
          formData.append("shopify_ve_client_secret", this.form.shopify_ve_client_secret.trim());
        }
        if (this.logoLightFile) {
          formData.append("logo_path", this.logoLightFile);
        }
        if (this.logoDarkFile) {
          formData.append("logo_path_dark", this.logoDarkFile);
        }

        await this.$store.dispatch("settings/save", formData);
        this.$store.commit("settings/SET_CHAT_ENABLED", this.form.chat_enabled);
        this.$store.commit("settings/SET_SHOPIFY_ENABLED", this.form.shopify_enabled);
        this.$store.commit("settings/SET_KUBAPP_VE_ENABLED", this.form.kubapp_ve_enabled);
        this.hasGeminiApiKey = this.hasGeminiApiKey || !!this.form.gemini_api_key.trim();
        this.form.gemini_api_key = "";
        this.hasShopifyClientSecret =
          this.hasShopifyClientSecret || !!this.form.shopify_client_secret.trim();
        this.form.shopify_client_secret = "";
        this.hasConenviosPassword =
          this.hasConenviosPassword || !!this.form.conenvios_password.trim();
        this.form.conenvios_password = "";
        this.hasKubappPassWd =
          this.hasKubappPassWd || !!this.form.kubapp_pass_wd.trim();
        this.form.kubapp_pass_wd = "";
        this.hasKubappVePassword =
          this.hasKubappVePassword || !!this.form.kubapp_ve_password.trim();
        this.form.kubapp_ve_password = "";
        this.hasShopifyVeClientSecret =
          this.hasShopifyVeClientSecret || !!this.form.shopify_ve_client_secret.trim();
        this.form.shopify_ve_client_secret = "";
        await Swal.fire("Éxito", "Configuración guardada correctamente", "success");
        this.logoLightFile = null;
        this.logoDarkFile = null;
        document.querySelectorAll('input[type="file"]').forEach((el) => (el.value = ""));
      } catch (error) {
        console.error("Error al guardar:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || "Error al guardar la configuración",
          "error"
        );
      } finally {
        this.isSaving = false;
      }
    },
    escapeHtml(value) {
      return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    formatPaymentModesHtml(endpoint, paymentModes) {
      const endpointHtml = `<p class="text-xs text-muted mb-2"><code>${this.escapeHtml(endpoint)}</code></p>`;

      if (!Array.isArray(paymentModes) || paymentModes.length === 0) {
        return `${endpointHtml}<p class="text-sm mb-0">No se encontraron modos de pago en Shopify.</p>`;
      }

      const rows = paymentModes
        .map((mode) => {
          const code = this.escapeHtml(mode.code || "—");
          const name = this.escapeHtml(
            mode.name && mode.name !== mode.code ? mode.name : "—"
          );
          const source = this.escapeHtml(mode.source || "—");

          return `<tr><td><code>${code}</code></td><td>${name}</td><td class="text-xs">${source}</td></tr>`;
        })
        .join("");

      return `${endpointHtml}
        <div style="max-height: 360px; overflow: auto; text-align: left;">
          <table class="table table-sm table-striped mb-0">
            <thead>
              <tr>
                <th>Código Shopify</th>
                <th>Nombre</th>
                <th>Fuente</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    },
    async viewShopifyPaymentModes() {
      if (!this.form.shopify_store.trim()) {
        await Swal.fire("Error", "Ingresa el dominio de la tienda Shopify.", "error");
        return;
      }

      if (!this.hasShopifyToken) {
        await Swal.fire(
          "Error",
          "Genera el token de Shopify antes de consultar los modos de pago.",
          "error"
        );
        return;
      }

      this.isLoadingPaymentModes = true;

      try {
        const response = await shopifyService.getPaymentModes();

        if (!response?.data?.success) {
          throw new Error(response?.data?.message || "No se pudieron consultar los modos de pago.");
        }

        await Swal.fire({
          title: "Modos de pago Shopify",
          html: this.formatPaymentModesHtml(
            response.data.endpoint,
            response.data.payment_modes
          ),
          width: 720,
        });
      } catch (error) {
        console.error("Error al consultar modos de pago Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al consultar modos de pago Shopify.",
          "error"
        );
      } finally {
        this.isLoadingPaymentModes = false;
      }
    },
    async loadSiigoWebhooks() {
      this.webhookLoading = true;
      try {
        const response = await settingsService.getSiigoWebhooks();
        this.siigoWebhooks = Array.isArray(response.data?.webhooks) ? response.data.webhooks : [];
      } catch (error) {
        this.siigoWebhooks = [];
      } finally {
        this.webhookLoading = false;
      }
    },
    async createWebhook() {
      if (!this.webhookForm.url?.trim()) {
        await Swal.fire("URL requerida", "Indique la URL del webhook.", "warning");
        return;
      }

      this.webhookSaving = true;
      try {
        const response = await settingsService.createSiigoWebhook({
          application_id: this.webhookForm.application_id || "Siigo",
          topic: this.webhookForm.topic,
          url: this.webhookForm.url.trim(),
        });
        this.siigoWebhooks = response.data?.webhooks || [];
        await Swal.fire("Webhook creado", response.data?.message || "Suscripción registrada en Siigo.", "success");
      } catch (error) {
        await Swal.fire("Error", error?.response?.data?.message || "No se pudo crear el webhook.", "error");
      } finally {
        this.webhookSaving = false;
      }
    },
    async deleteWebhook(id) {
      const confirm = await Swal.fire({
        title: "Eliminar webhook",
        text: "¿Eliminar esta suscripción en Siigo?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
      });

      if (!confirm.isConfirmed) return;

      this.webhookDeletingId = id;
      try {
        const response = await settingsService.deleteSiigoWebhook(id);
        this.siigoWebhooks = response.data?.webhooks || [];
        await Swal.fire("Eliminado", response.data?.message || "Webhook eliminado correctamente.", "success");
      } catch (error) {
        await Swal.fire("Error", error?.response?.data?.message || "No se pudo eliminar el webhook.", "error");
      } finally {
        this.webhookDeletingId = null;
      }
    },
    async generateShopifyToken() {
      if (!this.form.shopify_store.trim()) {
        await Swal.fire("Error", "Ingresa el dominio de la tienda Shopify.", "error");
        return;
      }
      if (!this.form.shopify_client_id.trim()) {
        await Swal.fire("Error", "Ingresa el Client ID de Shopify.", "error");
        return;
      }
      if (!this.form.shopify_client_secret.trim() && !this.hasShopifyClientSecret) {
        await Swal.fire("Error", "Ingresa el Client Secret de Shopify.", "error");
        return;
      }

      this.isGeneratingToken = true;
      try {
        const formData = new FormData();
        formData.append("shopify_enabled", "1");
        formData.append("shopify_store", this.form.shopify_store.trim());
        formData.append("shopify_client_id", this.form.shopify_client_id.trim());
        if (this.form.shopify_client_secret.trim()) {
          formData.append("shopify_client_secret", this.form.shopify_client_secret.trim());
        }
        await this.$store.dispatch("settings/save", formData);
        this.hasShopifyClientSecret =
          this.hasShopifyClientSecret || !!this.form.shopify_client_secret.trim();

        const payload = {
          client_id: this.form.shopify_client_id.trim(),
        };

        if (this.form.shopify_client_secret.trim()) {
          payload.client_secret = this.form.shopify_client_secret.trim();
        }

        const response = await shopifyService.generateToken(payload);

        if (!response?.data?.success) {
          throw new Error(response?.data?.message || "No se pudo generar el token.");
        }

        this.hasShopifyToken = true;
        this.form.shopify_client_secret = "";
        await Swal.fire("Shopify", response.data.message || "Token generado correctamente.", "success");
      } catch (error) {
        console.error("Error al generar token Shopify:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al generar token Shopify.",
          "error"
        );
      } finally {
        this.isGeneratingToken = false;
      }
    },
    async generateShopifyVeToken() {
      if (!this.form.shopify_ve_enabled) {
        await Swal.fire("Error", "Activa la integración Shopify Venezuela primero.", "error");
        return;
      }
      if (!this.form.shopify_ve_store.trim()) {
        await Swal.fire("Error", "Ingresa el dominio de la tienda Shopify Venezuela.", "error");
        return;
      }
      if (!this.form.shopify_ve_client_id.trim()) {
        await Swal.fire("Error", "Ingresa el Client ID de Shopify Venezuela.", "error");
        return;
      }
      if (!this.form.shopify_ve_client_secret.trim() && !this.hasShopifyVeClientSecret) {
        await Swal.fire("Error", "Ingresa el Client Secret de Shopify Venezuela.", "error");
        return;
      }

      this.isGeneratingShopifyVeToken = true;
      try {
        const formData = new FormData();
        formData.append("shopify_ve_enabled", "1");
        formData.append("shopify_ve_store", this.form.shopify_ve_store.trim());
        formData.append("shopify_ve_client_id", this.form.shopify_ve_client_id.trim());
        if (this.form.shopify_ve_client_secret.trim()) {
          formData.append("shopify_ve_client_secret", this.form.shopify_ve_client_secret.trim());
        }
        await this.$store.dispatch("settings/save", formData);
        this.hasShopifyVeClientSecret =
          this.hasShopifyVeClientSecret || !!this.form.shopify_ve_client_secret.trim();

        const payload = {
          client_id: this.form.shopify_ve_client_id.trim(),
        };

        if (this.form.shopify_ve_client_secret.trim()) {
          payload.client_secret = this.form.shopify_ve_client_secret.trim();
        }

        const response = await shopifyVeService.generateToken(payload);

        if (!response?.data?.success) {
          throw new Error(response?.data?.message || "No se pudo generar el token.");
        }

        this.hasShopifyVeToken = true;
        this.form.shopify_ve_client_secret = "";
        await Swal.fire(
          "Shopify Venezuela",
          response.data.message || "Token generado correctamente.",
          "success"
        );
      } catch (error) {
        console.error("Error al generar token Shopify Venezuela:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al generar token Shopify Venezuela.",
          "error"
        );
      } finally {
        this.isGeneratingShopifyVeToken = false;
      }
    },
    async generateKubappToken() {
      if (!this.form.kubapp_enabled) {
        await Swal.fire("Error", "Activa la integración KUBAPP primero.", "error");
        return;
      }
      if (!this.form.kubapp_user_id.trim()) {
        await Swal.fire("Error", "Ingresa el usuario de KUBAPP.", "error");
        return;
      }
      if (!this.form.kubapp_pass_wd.trim() && !this.hasKubappPassWd) {
        await Swal.fire("Error", "Ingresa la contraseña de KUBAPP.", "error");
        return;
      }

      this.isGeneratingKubappToken = true;
      try {
        const formData = new FormData();
        formData.append("kubapp_enabled", "1");
        formData.append("kubapp_user_id", this.form.kubapp_user_id.trim());
        if (this.form.kubapp_pass_wd.trim()) {
          formData.append("kubapp_pass_wd", this.form.kubapp_pass_wd.trim());
        }
        await this.$store.dispatch("settings/save", formData);
        this.hasKubappPassWd =
          this.hasKubappPassWd || !!this.form.kubapp_pass_wd.trim();

        const response = await settingsService.generateKubappToken();

        if (!response?.data?.success) {
          throw new Error(response?.data?.message || "No se pudo generar el token.");
        }

        this.hasKubappToken = true;
        this.form.kubapp_pass_wd = "";
        await Swal.fire("KUBAPP", response.data.message || "Token generado correctamente.", "success");
      } catch (error) {
        console.error("Error al generar token KUBAPP:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al generar token KUBAPP.",
          "error"
        );
      } finally {
        this.isGeneratingKubappToken = false;
      }
    },
    async generateKubappVeToken() {
      if (!this.form.kubapp_ve_enabled) {
        await Swal.fire("Error", "Activa la integración KUBAPP VENEZUELA primero.", "error");
        return;
      }
      if (!this.form.kubapp_ve_email.trim()) {
        await Swal.fire("Error", "Ingresa el correo de KUBAPP VENEZUELA.", "error");
        return;
      }
      if (!this.form.kubapp_ve_password.trim() && !this.hasKubappVePassword) {
        await Swal.fire("Error", "Ingresa la contraseña de KUBAPP VENEZUELA.", "error");
        return;
      }

      this.isGeneratingKubappVeToken = true;
      try {
        const formData = new FormData();
        formData.append("kubapp_ve_enabled", "1");
        formData.append("kubapp_ve_email", this.form.kubapp_ve_email.trim());
        if (this.form.kubapp_ve_password.trim()) {
          formData.append("kubapp_ve_password", this.form.kubapp_ve_password.trim());
        }
        await this.$store.dispatch("settings/save", formData);
        this.hasKubappVePassword =
          this.hasKubappVePassword || !!this.form.kubapp_ve_password.trim();

        const response = await settingsService.generateKubappVeToken();

        if (!response?.data?.success) {
          throw new Error(response?.data?.message || "No se pudo generar el token.");
        }

        this.hasKubappVeToken = true;
        this.form.kubapp_ve_password = "";
        await Swal.fire("KUBAPP VENEZUELA", response.data.message || "Token generado correctamente.", "success");
      } catch (error) {
        console.error("Error al generar token KUBAPP VENEZUELA:", error);
        await Swal.fire(
          "Error",
          error?.response?.data?.message || error.message || "Error al generar token KUBAPP VENEZUELA.",
          "error"
        );
      } finally {
        this.isGeneratingKubappVeToken = false;
      }
    },
  },
};
</script>
